'use client'

import { useState } from 'react'
import { Shield, Key } from 'lucide-react'

interface TwoFactorVerificationProps {
  tempToken: string
  onVerify: (code: string, isBackupCode: boolean) => Promise<void>
  onCancel: () => void
  isLoading?: boolean
  error?: string | null
}

export default function TwoFactorVerification({
  tempToken,
  onVerify,
  onCancel,
  isLoading = false,
  error = null
}: TwoFactorVerificationProps) {
  const [code, setCode] = useState('')
  const [useBackupCode, setUseBackupCode] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await onVerify(code, useBackupCode)
  }

  return (
    <div className="w-full max-w-md" data-cy="2fa-verification-form">
      <div className="text-center mb-6">
        <div className="w-14 h-14 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Shield className="w-7 h-7 text-primary-600" />
        </div>
        <h2 className="text-xl font-bold text-gray-900">Dvoufaktorové ověření</h2>
        <p className="text-gray-500 mt-2 text-sm">
          {useBackupCode
            ? 'Zadejte jeden z vašich záložních kódů'
            : 'Zadejte kód z vaší autentizační aplikace'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm" role="alert">
            {error}
          </div>
        )}

        <div>
          <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
            {useBackupCode ? 'Záložní kód' : 'Ověřovací kód'}
          </label>
          <div className="relative">
            <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              id="code"
              type="text"
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors text-center tracking-widest font-mono"
              placeholder={useBackupCode ? 'XXXX-XXXX' : '000000'}
              maxLength={useBackupCode ? 9 : 6}
              autoComplete="one-time-code"
              autoFocus
              data-cy={useBackupCode ? 'backup-code-input' : '2fa-code-input'}
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={isLoading || code.length < (useBackupCode ? 9 : 6)}
          className="w-full bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          data-cy="2fa-submit-button"
        >
          {isLoading ? 'Ověřování...' : 'Ověřit'}
        </button>
      </form>

      <div className="mt-6 text-center space-y-3">
        <button
          type="button"
          onClick={() => {
            setUseBackupCode(!useBackupCode)
            setCode('')
          }}
          className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          data-cy="use-backup-code-link"
        >
          {useBackupCode
            ? 'Použít kód z aplikace'
            : 'Nemám přístup k aplikaci - použít záložní kód'}
        </button>

        <div>
          <button
            type="button"
            onClick={onCancel}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Zpět na přihlášení
          </button>
        </div>
      </div>
    </div>
  )
}
